import type { PortfolioLabComposition } from "../../../ctx";
import BigNumber from "bignumber.js";
import { kahanSum } from "@mdotm/utils/math";
import { BigNumberExt } from "$root/third-party-integrations/bigNumber";
import { LAB_MATH_PRECISION, MAX_WEIGHT_PRECISION, weightPrecisionForAum } from "../../../utils/math";

type FlatInstrument =
	| { kind: "direct"; compositionIndex: number; weight: BigNumber }
	| { kind: "nested"; compositionIndex: number; investmentInstrumentIndex: number; weight: BigNumber };

type AumLeaf = FlatInstrument & { monetaryAmount: BigNumber };

export function rescaleCompositionMonetaryWeights(
	composition: PortfolioLabComposition[],
	newAum: number,
): PortfolioLabComposition[] {
	const flattenInstruments = flatInstrumentWeights(composition);
	if (flattenInstruments.length === 0) {
		return composition;
	}
	const newAumBN = BigNumber(newAum);
	const aumDistribution = distributeAumByPortfolioShare(flattenInstruments, newAumBN);
	return applyAumDistributionToComposition(composition, flattenInstruments, aumDistribution, newAumBN);
}

// Monetary-only reconciliation for the weight-edit path. Weights are the source of truth and
// are NEVER touched here; monetary is re-derived so the parts sum exactly to the whole via
// largest-remainder distribution — the same machinery the AUM-change path uses.
//
// Target = trunc2(sum(weight) / 100 × aum), NOT `aum` itself: distributing `aum` would force
// sum(monetary) === aum even when weights sum to 90 %, destroying the legitimate "weights ≠ 100 %"
// red signal. Null / non-positive AUM → target 0 (preserves computeMonetaryWeight(w, null|0) === 0).
//
// Truncation (not half-up rounding) keeps the target consistent with the totals displays, which
// truncate at 2 dp: a 99.999 % total shows 99.99 % and the monetary total must agree (99.99, not
// 100.00). Each factor is noise-stripped BEFORE the exact multiplication — the weight sum onto
// the finest weight grid (MAX_WEIGHT_PRECISION), the AUM onto cents. Truncating the raw product
// instead would be wrong at high AUM: double noise in the product exceeds sub-cent scale there
// (e.g. ~1e-5 at $50B), so a true ….03 arriving as ….0299996 would truncate a real cent away.
export function reconcileMonetaryWeights(
	composition: PortfolioLabComposition[],
	aum: number | null,
): PortfolioLabComposition[] {
	const leaves = flatInstrumentWeights(composition);
	if (leaves.length === 0) {
		return composition;
	}

	const totalWeight = leaves.reduce((sum, leaf) => sum.plus(leaf.weight), BigNumber(0));
	const targetTotal =
		aum == null || aum <= 0
			? BigNumber(0)
			: totalWeight
					.dp(MAX_WEIGHT_PRECISION, BigNumber.ROUND_HALF_UP)
					.div(100)
					.times(BigNumber(aum).dp(2, BigNumber.ROUND_HALF_UP))
					.dp(2, BigNumber.ROUND_DOWN);

	const distribution = distributeAumByPortfolioShare(leaves, targetTotal);
	const aumLeaves: AumLeaf[] = leaves.map((leaf, i) => ({
		...leaf,
		monetaryAmount: BigNumber(distribution[i]),
	}));
	return assignMonetaryFromLeaves(composition, aumLeaves);
}

function flatInstrumentWeights(composition: PortfolioLabComposition[]): FlatInstrument[] {
	return composition.flatMap((c, compositionIndex): FlatInstrument[] => {
		if (c.type === "instrument") {
			return [{ kind: "direct", compositionIndex, weight: BigNumber(c.weight) }];
		}
		return c.instruments.map((i, investmentInstrumentIndex) => ({
			kind: "nested",
			compositionIndex,
			investmentInstrumentIndex,
			weight: BigNumber(i.weight),
		}));
	});
}

function distributeAumByPortfolioShare(instruments: FlatInstrument[], targetAum: BigNumber): number[] {
	const totalInstrumentWeight = instruments.reduce((sum, leaf) => sum.plus(leaf.weight), BigNumber(0));
	const instrumentCount = instruments.length;

	const proportionalMonetaryAmounts: BigNumber[] = totalInstrumentWeight.isZero()
		? Array(instrumentCount).fill(targetAum.div(instrumentCount))
		: instruments.map((leaf) => leaf.weight.div(totalInstrumentWeight).times(targetAum));

	return largestRemainderDistribute(proportionalMonetaryAmounts, targetAum);
}

function buildMonetaryMaps(aumLeaves: AumLeaf[]): {
	directMonetary: Map<number, BigNumber>;
	nestedMonetary: Map<number, Map<number, BigNumber>>;
} {
	const directMonetary = new Map<number, BigNumber>();
	const nestedMonetary = new Map<number, Map<number, BigNumber>>();
	aumLeaves.forEach((leaf) => {
		if (leaf.kind === "direct") {
			directMonetary.set(leaf.compositionIndex, leaf.monetaryAmount);
		} else {
			let portfolioInnerMonetary = nestedMonetary.get(leaf.compositionIndex);
			if (!portfolioInnerMonetary) {
				portfolioInnerMonetary = new Map();
				nestedMonetary.set(leaf.compositionIndex, portfolioInnerMonetary);
			}
			portfolioInnerMonetary.set(leaf.investmentInstrumentIndex, leaf.monetaryAmount);
		}
	});
	return { directMonetary, nestedMonetary };
}

function computePortfolioTotals(
	composition: PortfolioLabComposition[],
	nestedMonetary: Map<number, Map<number, BigNumber>>,
): Map<number, BigNumber> {
	const portfolioTotals = new Map<number, BigNumber>();
	composition.forEach((c, compIdx) => {
		if (c.type !== "portfolio") {
			return;
		}
		const inner = nestedMonetary.get(compIdx);
		portfolioTotals.set(
			compIdx,
			inner ? [...inner.values()].reduce((s, mw) => s.plus(mw), BigNumber(0)) : BigNumber(0),
		);
	});
	return portfolioTotals;
}

// Assign monetary weights to a composition from already-distributed leaves, leaving `weight`
// untouched. Shared by the AUM-change zero branch and the weight-edit reconciliation path.
function assignMonetaryFromLeaves(
	composition: PortfolioLabComposition[],
	aumLeaves: AumLeaf[],
): PortfolioLabComposition[] {
	const { directMonetary, nestedMonetary } = buildMonetaryMaps(aumLeaves);
	const portfolioTotals = computePortfolioTotals(composition, nestedMonetary);
	return assignMonetaryOnly(composition, directMonetary, nestedMonetary, portfolioTotals);
}

function assignMonetaryOnly(
	composition: PortfolioLabComposition[],
	directMonetary: Map<number, BigNumber>,
	nestedMonetary: Map<number, Map<number, BigNumber>>,
	portfolioTotals: Map<number, BigNumber>,
): PortfolioLabComposition[] {
	return composition.map((c, compIdx) => {
		if (c.type === "instrument") {
			return { ...c, monetaryWeight: directMonetary.get(compIdx)?.toNumber() ?? 0 };
		}
		const inner = nestedMonetary.get(compIdx);
		if (!inner) {
			return { ...c, monetaryWeight: 0, instruments: c.instruments.map((i) => ({ ...i, monetaryWeight: 0 })) };
		}
		const updatedInstruments = c.instruments.map((i, investmentInstrumentIndex) => ({
			...i,
			monetaryWeight: inner.get(investmentInstrumentIndex)?.toNumber() ?? 0,
		}));
		return { ...c, monetaryWeight: portfolioTotals.get(compIdx)?.toNumber() ?? 0, instruments: updatedInstruments };
	});
}

function distributeTopLevelWeights(
	composition: PortfolioLabComposition[],
	directMonetary: Map<number, BigNumber>,
	portfolioTotals: Map<number, BigNumber>,
	newAum: BigNumber,
	weightPrecision: number,
): number[] {
	const rawTopLevelWeights = composition.map((c, compIdx) => {
		const instrumentMonetaryWeight =
			c.type === "instrument"
				? (directMonetary.get(compIdx) ?? BigNumber(0))
				: (portfolioTotals.get(compIdx) ?? BigNumber(0));
		return instrumentMonetaryWeight.div(newAum).times(100);
	});
	return largestRemainderDistribute(rawTopLevelWeights, BigNumber(100), weightPrecision);
}

function buildOutputComposition(
	composition: PortfolioLabComposition[],
	directMonetary: Map<number, BigNumber>,
	nestedMonetary: Map<number, Map<number, BigNumber>>,
	portfolioTotals: Map<number, BigNumber>,
	topLevelWeights: number[],
	weightPrecision: number,
): PortfolioLabComposition[] {
	return composition.map((c, compIdx) => {
		const newWeight = topLevelWeights[compIdx];

		if (c.type === "instrument") {
			return { ...c, weight: newWeight, monetaryWeight: directMonetary.get(compIdx)?.toNumber() ?? 0 };
		}

		const portfoliosMap = nestedMonetary.get(compIdx);
		const totalMonetaryWeight = portfolioTotals.get(compIdx) ?? BigNumber(0);

		if (!portfoliosMap || totalMonetaryWeight.isZero()) {
			return {
				...c,
				weight: newWeight,
				monetaryWeight: 0,
				instruments: c.instruments.map((i) => ({ ...i, weight: 0, monetaryWeight: 0 })),
			};
		}

		const updatedInstrumentsWithMonetaryWeight = c.instruments.map((i, investmentInstrumentIndex) => ({
			...i,
			monetaryWeight: portfoliosMap.get(investmentInstrumentIndex)?.toNumber() ?? 0,
		}));

		// Distribute inner effective weights via largest-remainder so they sum exactly to the portfolio's outer weight.
		// Values are scaled to newWeight (not 100) so sum(amounts) === total, which largestRemainderDistribute requires.
		const rawEffectiveWeights = updatedInstrumentsWithMonetaryWeight.map((i) =>
			BigNumber(i.monetaryWeight).div(totalMonetaryWeight).times(newWeight),
		);
		const instrumentsPortfolioWeights = largestRemainderDistribute(
			rawEffectiveWeights,
			BigNumber(newWeight),
			weightPrecision,
		);

		return {
			...c,
			weight: newWeight,
			monetaryWeight: totalMonetaryWeight.toNumber(),
			instruments: updatedInstrumentsWithMonetaryWeight.map((i, idx) => ({
				...i,
				weight: instrumentsPortfolioWeights[idx],
			})),
		};
	});
}

function applyAumDistributionToComposition(
	composition: PortfolioLabComposition[],
	instruments: FlatInstrument[],
	aumDistribution: number[],
	newAum: BigNumber,
): PortfolioLabComposition[] {
	const attachAumToLeaves: AumLeaf[] = instruments.map((leaf, i) => ({
		...leaf,
		monetaryAmount: BigNumber(aumDistribution[i]),
	}));

	if (newAum.isZero()) {
		return assignMonetaryFromLeaves(composition, attachAumToLeaves);
	}

	const { directMonetary, nestedMonetary } = buildMonetaryMaps(attachAumToLeaves);
	const portfolioTotals = computePortfolioTotals(composition, nestedMonetary);
	// Weight precision scales with the new AUM so re-derived weights keep every cent
	// expressible (a fixed 4 dp destroyed finer weights on every AUM update).
	const weightPrecision = weightPrecisionForAum(newAum.toNumber());
	const topLevelWeights = distributeTopLevelWeights(
		composition,
		directMonetary,
		portfolioTotals,
		newAum,
		weightPrecision,
	);
	return buildOutputComposition(
		composition,
		directMonetary,
		nestedMonetary,
		portfolioTotals,
		topLevelWeights,
		weightPrecision,
	);
}

/**
 * Proportionally scales `weights` so they sum to `target`, exact up to one ULP of the returned
 * doubles — a 14-dp target (from a monetary edit) can need more significant digits than a double
 * holds. The value the user entered is never rounded, only the shares it is split into. The shares land on the `precision`-dp grid via
 * largest-remainder distribution, and the largest share also carries the sub-grid part of `target`,
 * so a single weight receives `target` unchanged. Negative-weight-safe — unlike `scaleAndNormalize`,
 * which clamps to [0, target]. When the current sum is 0, distributes `target` equally.
 */
export function distributeWeightsToTarget(weights: number[], target: number, precision: number): number[] {
	const count = weights.length;
	if (count === 0) {
		return [];
	}
	const exactTarget = BigNumber(target).dp(LAB_MATH_PRECISION);
	const gridTarget = exactTarget.dp(precision, BigNumber.ROUND_DOWN);
	const currentSum = weights.reduce((sum, weight) => sum.plus(weight), BigNumber(0));
	const proportional: BigNumber[] = currentSum.isZero()
		? Array<BigNumber>(count).fill(gridTarget.div(count))
		: weights.map((weight) => BigNumber(weight).div(currentSum).times(gridTarget));
	// Re-round each result onto the grid: largestRemainderDistribute's numeric residual step can
	// re-introduce sub-grid float noise at very small weights (e.g. ~1e-11 near the AUM ceiling).
	const shares = largestRemainderDistribute(proportional, gridTarget, precision).map((w) =>
		BigNumberExt.roundWithPrecision(w, precision),
	);
	const largest = shares.reduce((best, w, i) => (Math.abs(w) > Math.abs(shares[best]) ? i : best), 0);
	shares[largest] = BigNumber(shares[largest]).plus(exactTarget.minus(gridTarget)).toNumber();
	return shares;
}

function largestRemainderDistribute(amounts: BigNumber[], total: BigNumber, decimalPlaces = 2): number[] {
	const unit = BigNumber(1).shiftedBy(-decimalPlaces);
	const floored = amounts.map((a) => a.dp(decimalPlaces, BigNumber.ROUND_DOWN));
	const flooredSum = floored.reduce((s, f) => s.plus(f), BigNumber(0));
	const totalUnits = total.shiftedBy(decimalPlaces).integerValue(BigNumber.ROUND_HALF_UP);
	const flooredSumUnits = flooredSum.shiftedBy(decimalPlaces).integerValue(BigNumber.ROUND_HALF_UP);
	const remainderUnits = totalUnits.minus(flooredSumUnits).toNumber();
	const ranked = amounts
		.map((a, i) => ({ i, frac: a.minus(floored[i]) }))
		.sort((a, b) => b.frac.comparedTo(a.frac) ?? 0)
		.map(({ i }) => i);

	const result = [...floored];
	for (let k = 0; k < remainderUnits; k++) {
		result[ranked[k]] = result[ranked[k]].plus(unit);
	}

	const resultNumbers = result.map((r) => r.toNumber());
	const residual = total.toNumber() - kahanSum(resultNumbers);
	if (residual !== 0) {
		resultNumbers[ranked[0]] += residual;
	}
	return resultNumbers;
}
